import { ROUTES } from "@/routes/path"
import { ChevronLeft, MessageSquare, Plus, Database, Plug, LayoutDashboard } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { useState } from "react"
import type { LucideIcon } from "lucide-react"
import type React from "react"
import { cn } from "@/lib/utils"
import OrganizationSwitcher from "@/pages/dashboard/organization/components/organization-switcher"
import { RecentThreads } from "@/pages/dashboard/chat/components/recent-threads"

interface NavItemProps {
  to: string
  icon: LucideIcon
  children: React.ReactNode
  target?: string
  disableActive?: boolean
}

interface NavSection {
  to: string
  icon: LucideIcon
  label: string
  disableActive?: boolean
}

const topNavigation: NavSection[] = [
  {
    label: "New Chat",
    to: ROUTES.DASHBOARD.STUDIO.INDEX,
    icon: Plus,
    disableActive: true,
  },
]

const mainNavigation: NavSection[] = [
  {
    label: "Chats",
    to: ROUTES.DASHBOARD.STUDIO.LIST,
    icon: MessageSquare,
  },
  {
    label: "Resources",
    to: ROUTES.DASHBOARD.RESOURCES.INDEX,
    icon: Database,
  },
  {
    label: "Integrations",
    to: ROUTES.DASHBOARD.INTEGRATIONS.INDEX,
    icon: Plug,
  },
  {
    label: "Boards",
    to: ROUTES.DASHBOARD.BOARDS.LIST,
    icon: LayoutDashboard,
  },
]

export default function Sidebar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()

  function handleNavigation() {
    setIsMobileMenuOpen(false)
  }

  function MenuIcon({ className }: { className: string }) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        data-rtl-flip=""
        className={`size-5 text-gray-600 transition-transform dark:text-gray-300 ${className}`}
      >
        <path d="M6.83496 3.99992C6.38353 4.00411 6.01421 4.0122 5.69824 4.03801C5.31232 4.06954 5.03904 4.12266 4.82227 4.20012L4.62207 4.28606C4.18264 4.50996 3.81498 4.85035 3.55859 5.26848L3.45605 5.45207C3.33013 5.69922 3.25006 6.01354 3.20801 6.52824C3.16533 7.05065 3.16504 7.71885 3.16504 8.66301V11.3271C3.16504 12.2712 3.16533 12.9394 3.20801 13.4618C3.25006 13.9766 3.33013 14.2909 3.45605 14.538L3.55859 14.7216C3.81498 15.1397 4.18266 15.4801 4.62207 15.704L4.82227 15.79C5.03904 15.8674 5.31234 15.9205 5.69824 15.9521C6.01398 15.9779 6.383 15.986 6.83398 15.9902L6.83496 3.99992ZM18.165 11.3271C18.165 12.2493 18.1653 12.9811 18.1172 13.5702C18.0745 14.0924 17.9916 14.5472 17.8125 14.9648L17.7295 15.1415C17.394 15.8 16.8834 16.3511 16.2568 16.7353L15.9814 16.8896C15.5157 17.1268 15.0069 17.2285 14.4102 17.2773C13.821 17.3254 13.0893 17.3251 12.167 17.3251H7.83301C6.91071 17.3251 6.17898 17.3254 5.58984 17.2773C5.06757 17.2346 4.61294 17.1508 4.19531 16.9716L4.01855 16.8896C3.36014 16.5541 2.80898 16.0434 2.4248 15.4169L2.27051 15.1415C2.03328 14.6758 1.93158 14.167 1.88281 13.5702C1.83468 12.9811 1.83496 12.2493 1.83496 11.3271V8.66301C1.83496 7.74072 1.83468 7.00898 1.88281 6.41985C1.93157 5.82309 2.03329 5.31432 2.27051 4.84856L2.4248 4.57317C2.80898 3.94666 3.36012 3.436 4.01855 3.10051L4.19531 3.0175C4.61285 2.83843 5.06771 2.75548 5.58984 2.71281C6.17898 2.66468 6.91071 2.66496 7.83301 2.66496H12.167C13.0893 2.66496 13.821 2.66468 14.4102 2.71281C15.0069 2.76157 15.5157 2.86329 15.9814 3.10051L16.2568 3.25481C16.8833 3.63898 17.394 4.19012 17.7295 4.84856L17.8125 5.02531C17.9916 5.44285 18.0745 5.89771 18.1172 6.41985C18.1653 7.00898 18.165 7.74072 18.165 8.66301V11.3271ZM8.16406 15.995H12.167C13.1112 15.995 13.7794 15.9947 14.3018 15.9521C14.8164 15.91 15.1308 15.8299 15.3779 15.704L15.5615 15.6015C15.9797 15.3451 16.32 14.9774 16.5439 14.538L16.6299 14.3378C16.7074 14.121 16.7605 13.8478 16.792 13.4618C16.8347 12.9394 16.835 12.2712 16.835 11.3271V8.66301C16.835 7.71885 16.8347 7.05065 16.792 6.52824C16.7605 6.14232 16.7073 5.86904 16.6299 5.65227L16.5439 5.45207C16.32 5.01264 15.9796 4.64498 15.5615 4.3886L15.3779 4.28606C15.1308 4.16013 14.8165 4.08006 14.3018 4.03801C13.7794 3.99533 13.1112 3.99504 12.167 3.99504H8.16406C8.16407 3.99667 8.16504 3.99829 8.16504 3.99992L8.16406 15.995Z"></path>
      </svg>
    )
  }

  function NavItem({ to, icon: Icon, children, target, disableActive }: NavItemProps) {
    const isActive = !disableActive && location.pathname === to

    return (
      <Link
        to={to}
        onClick={handleNavigation}
        target={target}
        className={`flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-gray-100 text-gray-900 dark:bg-[#1F1F23] dark:text-white"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-[#1F1F23] dark:hover:text-white"
        } ${isCollapsed ? "justify-center" : ""}`}
        title={isCollapsed ? (children as string) : undefined}
      >
        <Icon className={`size-4 shrink-0 ${isCollapsed ? "" : "mr-3"}`} />
        {!isCollapsed && children}
      </Link>
    )
  }

  return (
    <>
      <button
        type="button"
        className="fixed left-4 top-2 z-20 rounded-lg bg-white p-2 shadow-md dark:bg-[#0F0F12] xl:hidden"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-5 text-gray-600 dark:text-gray-300"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
        </svg>
      </button>
      <nav
        className={`fixed inset-y-0 left-0 z-30 bg-zinc-50 transition-all duration-200 ease-in-out dark:bg-[#0F0F12] xl:static xl:translate-x-0 ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} ${isCollapsed ? "xl:w-16" : "xl:w-64"} w-64`}
      >
        <div className="flex h-full flex-col">
          <div className={`flex h-14 items-center justify-between px-4`}>
            <div className="group relative flex items-center font-semibold">
              <Link to="#" className="flex items-center">
                <img
                  src="https://logos.ledgerbeam.com/ledgerbeam.com"
                  alt="Ledgerbeam"
                  width={30}
                  height={30}
                  className={cn(
                    "block shrink-0 rounded-md transition-opacity duration-200",
                    isCollapsed ? "xl:group-hover:opacity-0" : ""
                  )}
                />
              </Link>
              {/* Expand button that appears on hover when collapsed */}
              {isCollapsed && (
                <button
                  type="button"
                  onClick={() => setIsCollapsed(false)}
                  className="absolute left-0 top-1/2 hidden -translate-y-1/2 rounded-lg bg-white p-1.5 shadow-md transition-all duration-200 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 xl:group-hover:flex"
                  aria-label="Expand sidebar"
                >
                  <MenuIcon className="hover:cursor-e-resize" />
                </button>
              )}
            </div>

            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-3 transition-colors hover:bg-gray-50 dark:hover:bg-[#1F1F23] xl:hidden"
                aria-label="Close sidebar"
              >
                <ChevronLeft className="size-5 text-gray-600 dark:text-gray-300" />
              </button>

              {!isCollapsed && (
                <button
                  type="button"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="hidden rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-[#1F1F23] xl:flex"
                  aria-label="Toggle sidebar"
                >
                  <MenuIcon className="hover:cursor-w-resize" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-2 flex-1 overflow-y-auto px-2 pb-3">
            {/* Fixed New Chat link */}
            <div className="sticky top-0 z-10 bg-zinc-50 dark:bg-[#0F0F12]">
              {topNavigation.map((item) => (
                <NavItem key={item.label} to={item.to} icon={item.icon} disableActive={item.disableActive}>
                  {item.label}
                </NavItem>
              ))}
            </div>

            <div className="space-y-6">
              {/* Main Navigation */}
              <div className="">
                {mainNavigation.map((item) => (
                  <NavItem key={item.label} to={item.to} icon={item.icon} disableActive={item.disableActive}>
                    {item.label}
                  </NavItem>
                ))}
              </div>

              {/* Recents */}
              {!isCollapsed && <RecentThreads />}
            </div>
          </div>

          <OrganizationSwitcher isCollapsed={isCollapsed} />
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-20 bg-black/50 xl:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}
    </>
  )
}
